// Block type registry. Adding a new block should only require adding an entry
// here (plus a texture recipe in the render layer keyed by the same id).

export const enum Block {
  Air = 0,
  Grass = 1,
  Dirt = 2,
  Stone = 3,
  Sand = 4,
  Water = 5,
  Wood = 6,
  Leaves = 7,
  Planks = 8,
  Cobblestone = 9,
  Torch = 10,
  Bedrock = 11,
  CraftingTable = 12,
}

export interface BlockDef {
  id: Block;
  name: string;
  /** Solid blocks collide with entities and occlude neighbouring faces. */
  solid: boolean;
  /** Transparent blocks do not hide the faces of solid neighbours. */
  transparent: boolean;
  /** Seconds to break by hand (Infinity = unbreakable). */
  hardness: number;
  /** Emitted light level 0-15 (used by simple torch glow). */
  light: number;
  /** Whether this block can be selected/placed by the player. */
  placeable: boolean;
}

const DEFS: Record<Block, BlockDef> = {
  [Block.Air]: {
    id: Block.Air,
    name: 'Air',
    solid: false,
    transparent: true,
    hardness: 0,
    light: 0,
    placeable: false,
  },
  [Block.Grass]: {
    id: Block.Grass,
    name: 'Grass',
    solid: true,
    transparent: false,
    hardness: 0.6,
    light: 0,
    placeable: true,
  },
  [Block.Dirt]: {
    id: Block.Dirt,
    name: 'Dirt',
    solid: true,
    transparent: false,
    hardness: 0.5,
    light: 0,
    placeable: true,
  },
  [Block.Stone]: {
    id: Block.Stone,
    name: 'Stone',
    solid: true,
    transparent: false,
    hardness: 1.5,
    light: 0,
    placeable: true,
  },
  [Block.Sand]: {
    id: Block.Sand,
    name: 'Sand',
    solid: true,
    transparent: false,
    hardness: 0.5,
    light: 0,
    placeable: true,
  },
  [Block.Water]: {
    id: Block.Water,
    name: 'Water',
    solid: false,
    transparent: true,
    hardness: Infinity,
    light: 0,
    placeable: false,
  },
  [Block.Wood]: {
    id: Block.Wood,
    name: 'Wood',
    solid: true,
    transparent: false,
    hardness: 1.0,
    light: 0,
    placeable: true,
  },
  [Block.Leaves]: {
    id: Block.Leaves,
    name: 'Leaves',
    solid: true,
    transparent: true,
    hardness: 0.2,
    light: 0,
    placeable: true,
  },
  [Block.Planks]: {
    id: Block.Planks,
    name: 'Planks',
    solid: true,
    transparent: false,
    hardness: 0.8,
    light: 0,
    placeable: true,
  },
  [Block.Cobblestone]: {
    id: Block.Cobblestone,
    name: 'Cobblestone',
    solid: true,
    transparent: false,
    hardness: 1.5,
    light: 0,
    placeable: true,
  },
  [Block.Torch]: {
    id: Block.Torch,
    name: 'Torch',
    solid: false,
    transparent: true,
    hardness: 0.1,
    light: 14,
    placeable: true,
  },
  [Block.Bedrock]: {
    id: Block.Bedrock,
    name: 'Bedrock',
    solid: true,
    transparent: false,
    hardness: Infinity,
    light: 0,
    placeable: false,
  },
  [Block.CraftingTable]: {
    id: Block.CraftingTable,
    name: 'Crafting Table',
    solid: true,
    transparent: false,
    hardness: 1.2,
    light: 0,
    placeable: true,
  },
};

export function blockDef(id: Block): BlockDef {
  return DEFS[id];
}

export function isSolid(id: Block): boolean {
  return DEFS[id].solid;
}

export function isTransparent(id: Block): boolean {
  return DEFS[id].transparent;
}

/** Blocks that can be selected in the temporary build palette. */
export const PLACEABLE_BLOCKS: Block[] = (Object.values(DEFS) as BlockDef[])
  .filter((d) => d.placeable)
  .map((d) => d.id);
